import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  Download, 
  Edit, 
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  User,
  CreditCard,
  MapPin,
  Utensils,
  Music,
  Monitor,
  Info,
  Beer,
  Database,
  Euro
} from 'lucide-react';

const SheetViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, canEdit } = useAuth();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSheet();
  }, [id]);

  const loadSheet = async () => {
    try {
      const response = await api.get(`/function-sheets/${id}`);
      setSheet(response.data);
    } catch (error) {
      toast.error('Function Sheet nicht gefunden');
      navigate('/sheets');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/function-sheets/${id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FunctionSheet_${sheet.va_facts.title || 'Veranstaltung'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen!');
    } catch (error) {
      toast.error('Download fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-burgundy">Wird geladen...</div>
      </div>
    );
  }

  if (!sheet) return null;

  const { va_facts } = sheet;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sheet-view-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 no-print">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/sheets')}
            className="text-charcoal-600 hover:text-burgundy"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
        </div>
        
        <div className="flex gap-3">
          {canEdit() && (
            <Button
              variant="outline"
              onClick={() => navigate(`/editor/${id}`)}
              className="border-stone-300"
              data-testid="edit-btn"
            >
              <Edit className="h-4 w-4 mr-2" />
              Bearbeiten
            </Button>
          )}
          <Button
            onClick={handleDownload}
            className="bg-burgundy hover:bg-burgundy-800 text-white"
            data-testid="download-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            PDF herunterladen
          </Button>
        </div>
      </div>

      {/* Function Sheet Preview */}
      <div className="function-sheet-preview mx-auto bg-white rounded-lg border border-stone-200">
        {/* Header */}
        <div className="text-center border-b-2 border-burgundy pb-6 mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Beer className="h-8 w-8 text-gold" />
            <div>
              <h1 className="font-serif text-2xl font-bold text-burgundy">TUCHER MAUTKELLER</h1>
              <p className="text-xs tracking-widest text-charcoal-500 uppercase">Brauhaus Nürnberg</p>
            </div>
          </div>
          <h2 className="font-serif text-xl text-gold mt-4">Function Sheet</h2>
          <Badge 
            className={`mt-2 ${sheet.status === 'published' 
              ? 'bg-emerald-100 text-emerald-700' 
              : 'bg-gold-100 text-gold-700'
            }`}
          >
            {sheet.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
          </Badge>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="font-serif text-2xl font-bold text-burgundy">
            {va_facts.title || 'Veranstaltung'}
          </h2>
        </div>

        {/* Key Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 p-4 bg-stone-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Datum</p>
              <p className="font-semibold text-charcoal-800">{va_facts.event_date || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Uhrzeit</p>
              <p className="font-semibold text-charcoal-800">{va_facts.event_time || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Gäste</p>
              <p className="font-semibold text-charcoal-800">{va_facts.guest_count || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Kunde</p>
              <p className="font-semibold text-charcoal-800">{va_facts.customer || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Veranstaltungsort</p>
              <p className="font-semibold text-charcoal-800">
                {va_facts.venue_location || '-'}
                {va_facts.venue_exclusive && <span className="ml-1 text-gold text-xs">(exklusiv)</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Bezahlung</p>
              <p className="font-semibold text-charcoal-800">{va_facts.payment_method || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Ansprechpartner TMK</p>
              <p className="font-semibold text-charcoal-800">{va_facts.contact_tmk || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Ansprechpartner Kunde</p>
              <p className="font-semibold text-charcoal-800">{va_facts.contact_person || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Database className="h-5 w-5 text-burgundy flex-shrink-0" />
            <div>
              <p className="text-xs text-charcoal-500 uppercase tracking-wide">Kunde in Gastronovi</p>
              <p className="font-semibold text-charcoal-800">
                {va_facts.gastronovi_customer || 'Nein'}
                {va_facts.gastronovi_customer === 'Ja' && va_facts.gastronovi_customer_number && (
                  <span className="ml-1 text-gold text-xs">(Nr: {va_facts.gastronovi_customer_number})</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Venue Notes */}
        {va_facts.venue_notes && (
          <div className="mb-6">
            <h3 className="font-serif text-lg font-semibold text-burgundy mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Platzierungswünsche
            </h3>
            <div className="p-4 bg-stone-50 rounded-lg">
              <p className="text-charcoal-700 whitespace-pre-line">{va_facts.venue_notes}</p>
            </div>
          </div>
        )}

        {/* Contact Details & Billing */}
        {(va_facts.contact_details || va_facts.billing_address) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {va_facts.contact_details && (
              <div>
                <h3 className="font-serif text-lg font-semibold text-burgundy mb-2 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Kontakt
                </h3>
                <p className="text-charcoal-600 whitespace-pre-line">{va_facts.contact_details}</p>
              </div>
            )}
            {va_facts.billing_address && (
              <div>
                <h3 className="font-serif text-lg font-semibold text-burgundy mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Rechnungsadresse
                </h3>
                <p className="text-charcoal-600 whitespace-pre-line">{va_facts.billing_address}</p>
              </div>
            )}
          </div>
        )}

        {/* Abrechnen, brutto */}
        {(va_facts.billing_min_pax || va_facts.billing_menu_price || va_facts.billing_drinks || va_facts.billing_technical || 
          va_facts.billing_deco || va_facts.billing_latenight || va_facts.billing_artists || va_facts.billing_other) && (
          <div className="mb-6">
            <h3 className="font-serif text-lg font-semibold text-burgundy mb-3 flex items-center gap-2">
              <Euro className="h-4 w-4" />
              Abrechnen, brutto
            </h3>
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {va_facts.billing_min_pax && (
                  <div>
                    <span className="text-charcoal-500">Mind. Pax:</span>
                    <span className="ml-2 font-semibold text-charcoal-800">{va_facts.billing_min_pax}</span>
                  </div>
                )}
                {va_facts.billing_menu_price && (
                  <div>
                    <span className="text-charcoal-500">Menüpreis:</span>
                    <span className="ml-2 font-semibold text-charcoal-800">{va_facts.billing_menu_price}</span>
                  </div>
                )}
                {va_facts.billing_drinks && (
                  <div>
                    <span className="text-charcoal-500">Getränkepauschale:</span>
                    <span className="ml-2 font-semibold text-charcoal-800">{va_facts.billing_drinks}</span>
                  </div>
                )}
                {va_facts.billing_technical && (
                  <div>
                    <span className="text-charcoal-500">Technik:</span>
                    <span className="ml-2 font-semibold text-charcoal-800">{va_facts.billing_technical}</span>
                  </div>
                )}
                {va_facts.billing_deco && (
                  <div>
                    <span className="text-charcoal-500">Deko:</span>
                    <span className="ml-2 font-semibold text-charcoal-800">{va_facts.billing_deco}</span>
                  </div>
                )}
                {va_facts.billing_latenight && (
                  <div>
                    <span className="text-charcoal-500">Latenight-Fee:</span>
                    <span className="ml-2 font-semibold text-charcoal-800">{va_facts.billing_latenight}</span>
                  </div>
                )}
                {va_facts.billing_artists && (
                  <div>
                    <span className="text-charcoal-500">Künstler:</span>
                    <span className="ml-2 font-semibold text-charcoal-800">{va_facts.billing_artists}</span>
                  </div>
                )}
                {va_facts.billing_other && (
                  <div className="col-span-2 md:col-span-3">
                    <span className="text-charcoal-500">Sonstiges:</span>
                    <span className="ml-2 font-semibold text-charcoal-800">{va_facts.billing_other}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <Separator className="my-6" />

        {/* Schedule */}
        {va_facts.schedule && (
          <div className="mb-6">
            <h3 className="font-serif text-lg font-semibold text-burgundy mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Ablauf
            </h3>
            <div className="p-4 bg-stone-50 rounded-lg">
              <p className="text-charcoal-700 whitespace-pre-line">{va_facts.schedule}</p>
            </div>
          </div>
        )}

        {/* Artists & Technical */}
        {(va_facts.artists || va_facts.technical) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {va_facts.artists && (
              <div>
                <h3 className="font-serif text-lg font-semibold text-burgundy mb-3 flex items-center gap-2">
                  <Music className="h-4 w-4" />
                  Künstler
                </h3>
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-charcoal-700 whitespace-pre-line">{va_facts.artists}</p>
                </div>
              </div>
            )}
            {va_facts.technical && (
              <div>
                <h3 className="font-serif text-lg font-semibold text-burgundy mb-3 flex items-center gap-2">
                  <Monitor className="h-4 w-4" />
                  Technik
                </h3>
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-charcoal-700 whitespace-pre-line">{va_facts.technical}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Other Info */}
        {va_facts.other_info && (
          <div className="mb-6">
            <h3 className="font-serif text-lg font-semibold text-burgundy mb-3 flex items-center gap-2">
              <Info className="h-4 w-4" />
              Sonstiges
            </h3>
            <div className="p-4 bg-gold-50 border border-gold-200 rounded-lg">
              <p className="text-charcoal-700 whitespace-pre-line">{va_facts.other_info}</p>
            </div>
          </div>
        )}

        {/* Menu - at the end, with better formatting */}
        {va_facts.menu && (
          <div className="mb-6 mt-8 pt-6 border-t-2 border-burgundy">
            <h3 className="font-serif text-xl font-bold text-burgundy mb-4 flex items-center justify-center gap-2">
              <Utensils className="h-5 w-5" />
              Menü
            </h3>
            <div className="p-6 bg-stone-50 rounded-lg menu-content">
              {/* Check if menu contains HTML (from WYSIWYG editor) */}
              {va_facts.menu.includes('<') && va_facts.menu.includes('>') ? (
                <div 
                  className="prose prose-sm max-w-none text-charcoal-700
                    [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-burgundy [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:border-b [&_h2]:border-stone-200 [&_h2]:pb-1 [&_h2]:first:mt-0
                    [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-burgundy [&_h3]:mt-3 [&_h3]:mb-1
                    [&_strong]:font-bold [&_strong]:text-burgundy
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
                    [&_li]:py-0.5
                    [&_p]:my-1
                    [&_em]:italic"
                  dangerouslySetInnerHTML={{ __html: va_facts.menu }}
                />
              ) : (
                // Legacy plain text format
                va_facts.menu.split('\n').map((line, index) => {
                  const trimmedLine = line.trim();
                  if (!trimmedLine) return <div key={index} className="h-3" />;
                  
                  // Check if it's a category header
                  const categoryPatterns = [
                    /^(aperitif|vorspeise|hauptgang|hauptgericht|dessert|nachspeise|getränke|drinks|wein|bier|suppe|salat|menü|gang|buffet|speisen)/i,
                    /^.{0,30}:$/,
                    /^(brauhaus-menü|hausgemacht|vegetarisch|vegan)/i,
                    /^\d+\.\s*(gang|vorspeise|hauptgang)/i,
                  ];
                  
                  const isCategory = 
                    categoryPatterns.some(pattern => pattern.test(trimmedLine)) ||
                    (trimmedLine.includes(':') && trimmedLine.indexOf(':') < 35 && !trimmedLine.startsWith('-')) ||
                    (trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length < 40 && trimmedLine.length > 3) ||
                    (trimmedLine.startsWith('**') && trimmedLine.endsWith('**'));
                  
                  const looksLikeHeader = !trimmedLine.startsWith('-') && 
                    !trimmedLine.startsWith('•') && 
                    trimmedLine.length < 50 &&
                    (trimmedLine.endsWith(':') || /^[A-ZÄÖÜ]/.test(trimmedLine) && /[:-]/.test(trimmedLine));
                  
                  if (isCategory || looksLikeHeader) {
                    const categoryText = trimmedLine
                      .replace(/^\*\*|\*\*$/g, '')
                      .replace(/:$/, '');
                    return (
                      <p key={index} className="font-bold text-burgundy mt-4 mb-2 first:mt-0 text-sm uppercase tracking-wide">
                        {categoryText}
                      </p>
                    );
                  } else {
                    const itemText = trimmedLine.replace(/^[-•·]\s*/, '');
                    return (
                      <p key={index} className="text-charcoal-700 pl-4 py-0.5 text-sm">
                        • {itemText}
                      </p>
                    );
                  }
                })
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-stone-200 text-center text-xs text-charcoal-400">
          <p>Erstellt von {sheet.created_by_name} am {new Date(sheet.created_at).toLocaleDateString('de-DE')}</p>
          <p className="mt-1">Zuletzt aktualisiert: {new Date(sheet.updated_at).toLocaleDateString('de-DE')}</p>
        </div>
      </div>
    </div>
  );
};

export default SheetViewPage;
